import { useLazyQuery } from '@apollo/react-hooks'
import Spacer from '@berlitz/spacer'
import SchedulingCalendar, {
  DateRanges,
  StringRange,
  ISelectedLabels,
} from '@berlitzplatforms/micro.ui.scheduling-calendar/lib'
import PortalStudentProfile from '@classes/StudentProfile'
import Error from '@components/Error'
import HR from '@components/HR'
import MenuTab, { MenuTabItem } from '@components/MenuTab'
import { WarningModal } from '@components/Modal'
import Scroller from '@components/Scroller'
import Select from '@components/Select'
import { BookingPossibilityQueryResult } from '@interfaces/BookingPossibilities'
import { InstructorsQueryResult } from '@interfaces/Instructor'
import { ScheduleSession } from '@interfaces/ScheduleSession'
import { MenuTabBox } from '@layouts/LearningPath/DetailTabs/style'
import { GET_BOOKING_POSSIBILITIES } from '@queries/getBookingPossibilities'
import { GET_SCHEDULES_ID } from '@queries/index'
import { CANCELLED_LESSON_STATUS, DATE_FORMAT, MINS_PER_UNIT } from '@utils/constants'
import { flatten, groupBy, isEmpty, omit } from 'lodash'
import moment from 'moment-timezone'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import { Tab } from '../ManageInstructorView/style'
import LegendItem from './LegendItem'
import menu from './menu'
import PageHeader, { GenericOption } from './PageHeader'
import { Sidebar, BookedLesson, TBookedLessonStatus, TDeleteLessonStatus } from './Sidebar'
import {
  AvailableTimeSlot,
  LeftContent,
  LegendWrapper,
  MainContent,
  Session,
  SideContent,
  StyledSelect,
  UnavailableTimeSlot,
  UpperSection,
} from './style'

import { InstructorHours, InstructorHoursQueryRes } from '@interfaces/InstructorHours'
import { LearningCenterHoliday } from '@interfaces/LearningCenter'
import { GET_INSTRUCTOR_HOURS } from '@queries/instructorHours'
import { GET_LC_HOLIDAYS } from '@queries/learningCenters'
import {
  getDateRangeGroupings,
  getHoursInARowRestrictions,
  getSelectedIntervals,
} from '@utils/helpers/instructor-scheduling'
import { IGetEventAttendee, IGetEventAttendeeData, IGetEventsInput } from '@interfaces/Events'
import { GET_EVENT_ATTENDEE } from '@queries/events'
import { utcfyDate } from '@layouts/Events/helpers'
import { ATTENDEE_STATUS } from '@utils/constants/events'
import { isGodMode } from '@utils/helpers'
import useRefetchProfile from '@hooks/useRefetchProfile'
import { IInstructorLessons, IInsturctorRestrictions } from './interface'
import { CrossInstrcutorContext, FMIStudentProfileContext } from '../context'
import { updateDHRemaining } from './Sidebar/helpers'
import { useFMIBulkForm } from '../BulkForm/Form/useFMIBulkForm'
import { useDispatch } from 'react-redux'
import {
  updateFMIBulkFormInstructorTypes,
  updateFMIBulkFormInstructors,
  updateFMIBulkFormSelectedDate,
} from '@redux/actions/fmiBulkForm'
import Instructors from './Instructors'
import useTimeFormat from '@hooks/useTimeFormat'

export interface InstructorAvailabilityProps {
  loading: boolean
  instructors?: InstructorsQueryResult
}

const InstructorAvailability: React.FC<InstructorAvailabilityProps> = ({
  loading: pageLoading,
  instructors: instructorData,
}) => {
  const intl = useIntl()
  const router = useRouter()
  const dispatch = useDispatch()
  const refetchProfileAfter = useRefetchProfile()
  const { state } = useContext(CrossInstrcutorContext)
  const {
    userProfile: profile,
    EventRecordId,
    IsRegGroupProgram,
    isAdmin: isRegionalUser,
    isSessionAdmin: isAdmin,
    baseRoute,
    getRouteParams,
    refetchUserProfile,
  } = useContext(FMIStudentProfileContext)
  const { isBulkOnlyFeature, selectedInstructors = [], selectedDate } = useFMIBulkForm()
  const { getTimeFormat } = useTimeFormat()

  const godMode = isGodMode(isAdmin) || __DEV__
  const student = new PortalStudentProfile(profile?.StudentProfile?.StudentProfileInfo)
  const program = student.Programs[router.query.programId as string]
  const endDate = moment
    .tz(program?.program?.ContractEndDate, program?.program?.DeliveryLCTimeZone || profile.Timezone)
    .endOf('day')
    .toISOString()
  const isRebook = !isEmpty(router.query.sessionId)

  const [readOnly] = useState(program?.isReadOnly(isRebook))
  const [at, setAt] = useState(2)
  const [loading, setLoading] = useState(pageLoading)
  const [instructor, setInstructor] = useState(router.query.instructorId as string)
  const [instructors, setInstructors] = useState<GenericOption[]>()
  const [sessions, setSessions] = useState<DateRanges>([])
  const [events, setEvents] = useState<DateRanges>([])
  const [instructorEvents, setInstructorEvents] = useState<DateRanges>([])
  const [availabilities, setAvailabilities] = useState<DateRanges>([])
  const [unavailabilities, setUnavailabilities] = useState<DateRanges>([])
  const [restrictions, setRestrictions] = useState<DateRanges>([])
  const [instructorRestrictions, setInstructorRestrictions] = useState<IInsturctorRestrictions | undefined>({})
  const [viewDate, setViewDate] = useState<string>()
  const [month, setMonth] = useState<string | undefined>()
  const [months, setMonths] = useState<GenericOption[]>()
  const [disabled, setDisabled] = useState(false)
  const [initialLoad, setInitialLoad] = useState(0)
  const [error, setError] = useState(false)
  const [maxSelect, setMaxSelect] = useState(0)
  const [hoverRange, setHoverRange] = useState(MINS_PER_UNIT)
  const [loadingRanges, setLoadingRanges] = useState<DateRanges>()
  const [failedRanges, setFailedRanges] = useState<DateRanges>([])
  const [selected, setSelected] = useState<DateRanges>([])
  const [selectedLabels, setSelectedLabels] = useState<ISelectedLabels>()
  const [instructorLessons, setInstructorLessons] = useState<IInstructorLessons>()
  const [currentSelected, setCurrentSelected] = useState<StringRange>()
  const [deselected, setDeselected] = useState<StringRange>()
  const [lessons, setLessons] = useState<BookedLesson[]>([])
  const [notSaved, setNotSaved] = useState<boolean | string>(false)
  const [openWarningModal, setOpenWarningModal] = useState(false)
  const [toURL, setToUrl] = useState<string>()
  const [headerDefault, setHeaderDefault] = useState(router.query.instructorId as string)
  const [deleteAllSilently, setDeleteAllSilently] = useState(false)
  const [deletingStoredLessons, setDeletingStoredLessons] = useState(false)
  const [fetchingProfile, setFetchingProfile] = useState(false)

  const [numOfWeeks] = useState(53) // number of weeks before we refetch data
  const [range, setRange] = useState({
    start: moment.tz(profile.Timezone),
    end: moment.tz(profile.Timezone).add(numOfWeeks, 'weeks').weekday(7).startOf('day'),
  })
  const [week, setWeek] = useState((selectedDate ? moment(selectedDate) : moment.tz(profile.Timezone)).toISOString())
  const [instructorHours, setInstructorHours] = useState<InstructorHours>()
  const selectedIntervals = useMemo(() => getSelectedIntervals(selected), [selected])
  const queryInstructor = instructors?.find(({ value }) => value === instructor)
  const isCrossInstructorOnly = state.allowed && !isBulkOnlyFeature

  const [getIH, { data: IHData, loading: IHLoading, error: IHerror, refetch: IHRefetch }] =
    useLazyQuery<InstructorHoursQueryRes>(GET_INSTRUCTOR_HOURS, { fetchPolicy: 'no-cache' })

  const [
    getStudentSessions,
    { data: myCalendar, loading: myCalendarLoading, error: myCalendarError, refetch: studentSessionRefretch },
  ] = useLazyQuery<{
    getSchedulesId: ScheduleSession[]
  }>(GET_SCHEDULES_ID, { fetchPolicy: 'no-cache' })

  const [
    getInstructorSessions,
    {
      data: instructorSessions,
      loading: instructorSessionsLoading,
      error: instructorSessionsError,
      refetch: instructorSessionRefetch,
    },
  ] = useLazyQuery<{
    getSchedulesId: ScheduleSession[]
  }>(GET_SCHEDULES_ID, { fetchPolicy: 'no-cache' })

  const [
    getBookingPossibilities,
    { data: bookingP, loading: loadingBookingP, error: bookingPError, refetch: bpRefetch },
  ] = useLazyQuery<BookingPossibilityQueryResult>(GET_BOOKING_POSSIBILITIES, { fetchPolicy: 'no-cache' })

  const [getLCHolidays, { data: LCHolidays, loading: loadingLCHolidays, error: LCHolidaysError }] =
    useLazyQuery<LearningCenterHoliday>(GET_LC_HOLIDAYS)

  const [
    getEventAttendee,
    { data: userEventsData, loading: userEventsLoading, error: userEventsError, refetch: userEventsRefetch },
  ] = useLazyQuery<IGetEventAttendeeData, IGetEventsInput>(GET_EVENT_ATTENDEE, {
    fetchPolicy: 'network-only',
  })

  const [
    getInstructorEventAttendee,
    {
      data: instructorEventsData,
      loading: instructorEventsLoading,
      error: instructorEventsError,
      refetch: instructorEventsRefetch,
    },
  ] = useLazyQuery<IGetEventAttendeeData, IGetEventsInput>(GET_EVENT_ATTENDEE, {
    fetchPolicy: 'network-only',
  })

  const convertToDateRange = (dates) =>
    dates?.map((date) => [date.StartTime || date.StartDateTime, date.EndTime || date.EndDateTime])

  const prepSessions = (data?: ScheduleSession[]) => {
    return Object.entries(
      groupBy(
        data?.filter((item) => !CANCELLED_LESSON_STATUS.includes(item.Status?.toLowerCase())),
        'SessionId'
      )
    ).reduce((prev, curr) => {
      return [...prev, [curr[1][0].StartTime, curr[1][curr[1].length - 1].EndTime]] as DateRanges
    }, [] as DateRanges)
  }

  const prepEvents = (data?: IGetEventAttendee[]) =>
    IsRegGroupProgram
      ? []
      : Object.entries(
          groupBy(
            data?.filter(
              ({ berlitzEventAttendeeInfo }) => berlitzEventAttendeeInfo.AttendeeStatus !== ATTENDEE_STATUS.CANCELLED
            ),
            'berlitzEventAttendeeInfo.EventAttendeeId'
          )
        ).reduce((prev, curr) => {
          return [
            ...prev,
            [
              curr[1][0].berlitzEventAttendeeInfo.StartDatetime,
              curr[1][curr[1].length - 1].berlitzEventAttendeeInfo.EndDatetime,
            ],
          ] as DateRanges
        }, [] as DateRanges)

  const generateMonths = () => {
    const dateStart = moment.tz(moment(), profile.Timezone)
    const dateEnd = moment.tz(endDate, profile.Timezone)
    const interim = dateStart.clone()
    const timeValues: GenericOption[] = []

    while (dateEnd > interim || interim.format('M') === dateEnd.format('M')) {
      timeValues.push({
        label: interim.format(DATE_FORMAT.MY),
        value: interim.toISOString(),
      })
      interim.startOf('month').add(1, 'month')
    }

    return timeValues
  }

  const onMonthChange = (date) => {
    setMonth(
      months?.find(
        (item) =>
          moment.tz(item.value, profile.Timezone).format(DATE_FORMAT.MY) ===
          moment.tz(date.weekday(0).toISOString(), profile.Timezone).format(DATE_FORMAT.MY)
      )?.value || months?.[0]?.value
    )
  }

  const createTempHold = (data: StringRange) => {
    setCurrentSelected(data)
    setDisabled(true)
    setSelected([...selected, data])
    if (isCrossInstructorOnly || isBulkOnlyFeature) {
      setSelectedLabels({
        ...selectedLabels,
        [data?.[0]]: {
          id: instructor,
          label: queryInstructor?.label || '',
        },
      })
    }
    setLoadingRanges([data])
  }

  const removeTempHold = (data: StringRange) => {
    setDeselected(data)
  }

  function onSelectLesson(data: BookedLesson, lessonState: TBookedLessonStatus) {
    const selectInstructor = instructorLessons?.[instructor]

    if ((isCrossInstructorOnly || isBulkOnlyFeature) && selectInstructor) {
      setInstructorLessons({
        ...omit(instructorLessons || {}, [instructor]),
        [instructor]: {
          ...selectInstructor,
          lessons:
            lessonState === 'created'
              ? [...selectInstructor?.lessons, data]
                  .filter(({ Status }) => Status !== 'loading')
                  .map((selectedLesson) => ({
                    ...selectedLesson,
                    disabled: false,
                  }))
              : lessonState === 'error'
                ? [...selectInstructor?.lessons, data]
                    .map((selectedLesson) => ({
                      ...selectedLesson,
                      disabled: false,
                    }))
                    .filter(({ Status }) => Status !== 'loading')
                : [...selectInstructor?.lessons, data].map((selectedLesson) => ({
                    ...selectedLesson,
                    disabled: true,
                  })),
        },
      })
    }
  }

  function onDeselectLesson(data?: BookedLesson, lessonState?: TDeleteLessonStatus) {
    const instructorId = selectedLabels?.[data?.StartTime || '']?.id || data?.InstructorId || ''
    const selectInstructor = instructorLessons?.[instructorId || '']

    if ((isCrossInstructorOnly || isBulkOnlyFeature) && data && selectInstructor) {
      setInstructorLessons({
        ...omit(instructorLessons || {}, [instructorId]),
        [instructorId]: {
          ...selectInstructor,
          lessons:
            lessonState === 'deleted'
              ? selectInstructor?.lessons
                  .filter(
                    (selectedLesson) => selectedLesson?.Id !== data?.Id || selectedLesson?.StartTime !== data?.StartTime
                  )
                  .map((selectedLesson) => ({
                    ...selectedLesson,
                    disabled: false,
                  }))
              : selectInstructor?.lessons.map((selectedLesson) => ({
                  ...selectedLesson,
                  Status:
                    selectedLesson?.Id === data?.Id && lessonState === 'loading' ? lessonState : selectedLesson?.Status,
                  disabled: selectedLesson?.Id !== data?.Id,
                })),
        },
      })
      setSelectedLabels(omit(selectedLabels, data?.StartTime))
    }
  }

  function onDeselectAllLesson() {
    if (isCrossInstructorOnly || isBulkOnlyFeature) {
      setInstructorLessons({
        [instructor]: {
          name: queryInstructor?.label || '',
          lessons: [],
        },
      })
    }
  }

  const setters = {
    failed: (data: DateRanges) => setFailedRanges(data),
    loading: (data: DateRanges) => setLoadingRanges(data),
    maxSelect: (data: number) => setMaxSelect(data),
    lessons: (data: BookedLesson[]) => setLessons(data),
    selectLesson: onSelectLesson,
    deselectLesson: onDeselectLesson,
    deselectAllLesson: onDeselectAllLesson,
    current: (data: StringRange | undefined) => setCurrentSelected(data),
    deselected: (data: StringRange) => removeTempHold(data),
    disabled: (data: boolean) => setDisabled(data),
    deleteAllSilently: (data: boolean) => {
      setDeleteAllSilently(data)
      if (isCrossInstructorOnly || isBulkOnlyFeature) {
        setSelectedLabels(undefined)
      }
    },
    selected: (data: DateRanges) => setSelected(data),
    instructorHours: (data?: InstructorHours) => setInstructorHours(data),
    notSaved: (data: boolean) => setNotSaved(data),
    deleteStorage: (data: boolean) => setDeletingStoredLessons(data),
  }

  const fetchDetails = async () => {
    setFetchingProfile(true)
    const [updateUI] = await refetchProfileAfter()
    updateUI && (await updateUI())
    setFetchingProfile(false)

    const end = range.end.clone().add(2, 'day').startOf('day')
    const start = range.start.clone()

    getStudentSessions({
      variables: {
        Id: profile.SFId,
        FromDate: start.weekday(0).startOf('day').utc().format(),
        EndDate: end.toISOString(),
        FilterType: 'FILTER_STUDENT_MY_CALENDER',
        Limit: 10000,
        UserId: profile.SFId
      },
    })

    getBookingPossibilities({
      variables: {
        StartDateTime: start.utc().format().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        ProgramId: router.query.programId,
        InstructorId: instructor,
      },
    })

    getInstructorSessions({
      variables: {
        Id: instructor || router.query.instructorId,
        FromDate: start.utc().format(),
        EndDate: end.toISOString(),
        FilterType: 'FILTER_INSTRUCTOR_MY_CALENDER',
        Limit: 10000,
        UserId: profile.SFId
      },
    })

    getIH({
      variables: {
        StartDateTime: start.toISOString().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        LearningCenterId: program.program?.LearningCenterId,
        InstructorId: instructor || router.query.instructorId,
      },
    })

    getLCHolidays({
      variables: {
        StartDateTime: start.toISOString().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        LearningCenterId: program.program?.LearningCenterId,
        Country: program.program?.DeliveryLCCountry,
      },
    })

    getEventAttendee({
      variables: {
        StartDateTime: utcfyDate(start),
        EndDateTime: utcfyDate(
          moment(program?.program?.ContractEndDate, DATE_FORMAT.BASIC).tz(profile.Timezone).endOf('day')
        ),
        RecordId: EventRecordId,
        Type: profile.Type,
      },
    })

    getInstructorEventAttendee({
      variables: {
        StartDateTime: utcfyDate(start),
        EndDateTime: utcfyDate(
          moment(program?.program?.ContractEndDate, DATE_FORMAT.BASIC).tz(profile.Timezone).endOf('day')
        ),
        RecordId: instructor || (router.query.instructorId as string),
        Type: 'Instructor',
      },
    })
  }

  useEffect(() => {
    setDisabled(!!loadingRanges?.length)
  }, [loadingRanges])

  useEffect(() => {
    if (instructorHours && instructorSessions && LCHolidays?.getLCHolidays) {
      const maxedDays = instructorHours.instructorHoursInfo?.DailyHoursInfo?.filter((item) => {
        const dh = item.DailyHoursRemaining || 0
        return dh <= 0 || dh < hoverRange / 60
      })

      const HoursInARow = instructorHours?.instructorHoursInfo?.HoursInARow
      const MinimumBreakLength = instructorHours?.instructorHoursInfo?.MinimumBreakLength
      const selections =
        isCrossInstructorOnly || isBulkOnlyFeature
          ? instructorLessons?.[instructor]?.lessons?.map(({ StartTime, EndTime }): [string, string] => [
              StartTime,
              EndTime,
            ]) || []
          : selected

      const cutOffDate = program
        .getCutOffTime(profile.Timezone, program.program?.SchedulingMinutesBeforeCutoff)
        .toISOString()
      setUnavailabilities(
        getSelectedIntervals(
          flatten(
            getDateRangeGroupings(
              [
                ...selections,
                ...instructorEvents,
                ...(convertToDateRange(
                  instructorSessions?.getSchedulesId
                    ?.filter((item) => !CANCELLED_LESSON_STATUS.includes(item.Status?.toLowerCase()))
                    ?.filter(({ SessionId }) => SessionId !== router.query.sessionId)
                ) || []),
              ],
              true,
              hoverRange,
              HoursInARow,
              MinimumBreakLength
            )
          )
            .concat(
              convertToDateRange(maxedDays).concat(
                convertToDateRange(LCHolidays.getLCHolidays.Holidays?.map((holiday) => ({ ...holiday.holidayInfo })))
              )
            )
            .concat([[moment.tz(profile.Timezone).startOf('d').toISOString(), cutOffDate]])
        )
      )
    }
  }, [instructorSessions, JSON.stringify(instructorHours), JSON.stringify(LCHolidays), selected, instructorEvents])

  useEffect(() => {
    let getInstructorHours = IHData?.getInstructorHours

    if (getInstructorHours) {
      instructorLessons?.[instructor]?.lessons?.map((lesson) => {
        updateDHRemaining(
          godMode,
          getInstructorHours,
          moment.tz(lesson.StartTime, 'GMT'),
          moment.tz(lesson.EndTime, 'GMT'),
          false,
          (cbInstructorHours) => {
            getInstructorHours = {
              ...getInstructorHours,
              instructorHoursInfo: {
                ...getInstructorHours?.instructorHoursInfo,
                DailyHoursInfo: cbInstructorHours?.instructorHoursInfo?.DailyHoursInfo,
              },
            }
          }
        )
      })
    }

    setInstructorHours(getInstructorHours)
  }, [IHData])

  useEffect(() => {
    setAvailabilities(convertToDateRange(bookingP?.getBookingPossibilities?.bookingPossibilitiesDateRange))
  }, [bookingP])

  useEffect(() => {
    const events = prepEvents(userEventsData?.getEventAttendeev2 || [])
    const instructorEvents = prepEvents(instructorEventsData?.getEventAttendeev2 || [])
    setSessions([
      ...prepSessions(
        (myCalendar?.getSchedulesId || [])?.filter(({ SessionId }) => SessionId !== router.query.sessionId)
      ),
      ...events,
    ])
    setEvents(events)
    setInstructorEvents(instructorEvents)
  }, [myCalendar, userEventsData, instructorEventsData])

  useEffect(() => {
    if (!isEmpty(instructorHours)) {
      godMode && console.log(`HoursInARow: ${instructorHours?.instructorHoursInfo?.HoursInARow}`)
      godMode && console.log(`MaxDailyHours: ${instructorHours?.instructorHoursInfo?.MaxDailyHours}`)

      const selections =
        isCrossInstructorOnly || isBulkOnlyFeature
          ? instructorLessons?.[instructor]?.lessons?.map(({ StartTime, EndTime }): [string, string] => [
              StartTime,
              EndTime,
            ]) || []
          : selected

      const restricts = getSelectedIntervals(
        getHoursInARowRestrictions(
          [
            ...prepSessions(
              (myCalendar?.getSchedulesId || [])?.filter(
                ({ SessionId, InstructorId }) => SessionId !== router.query.sessionId && InstructorId === instructor
              )
            ),
            ...events,
            ...selections,
            ...(instructorSessions?.getSchedulesId || [])
              ?.filter((item) => !CANCELLED_LESSON_STATUS.includes(item.Status?.toLowerCase()))
              ?.filter(({ SessionId }) => SessionId !== router.query.sessionId)
              ?.map(({ StartTime, EndTime }) => [StartTime, EndTime] as StringRange),
          ],
          instructorHours?.instructorHoursInfo?.HoursInARow,
          instructorHours?.instructorHoursInfo?.MinimumBreakLength,
          program.program?.UnitsPerLesson,
          program.program?.LessonsPerSession
        )
      )

      if (isCrossInstructorOnly || isBulkOnlyFeature) {
        setInstructorRestrictions({
          ...instructorRestrictions,
          [instructor]: {
            restrictions: restricts,
          },
        })
      } else {
        setRestrictions(restricts)
      }
    }
  }, [
    JSON.stringify(instructorHours),
    sessions,
    events,
    selected,
    deleteAllSilently,
    fetchingProfile,
    instructor,
    myCalendar,
    instructorData,
    IHData,
    isCrossInstructorOnly,
    isBulkOnlyFeature,
  ])

  useEffect(() => {
    const cleanData = isBulkOnlyFeature
      ? selectedInstructors?.map((item) => ({
          id: item.InstructorId || '',
          label: item.Name || '',
          value: item.InstructorId || '',
        }))
      : instructorData?.getInstructorsByProgramId
          ?.map((item) => item.instructorInfo)
          ?.filter((item) => (!item.IsBlocked && item.IsPreferred) || item.InstructorId === router.query.instructorId)
          ?.map((item) => ({ id: item.InstructorId || '', label: item.Name || '', value: item.InstructorId || '' }))
    setInstructors(cleanData)
  }, [instructorData])

  useEffect(() => {
    setInitialLoad(initialLoad + 1)
  }, [pageLoading])

  useEffect(() => {
    if (initialLoad < 3) {
      setLoading(
        pageLoading ||
          myCalendarLoading ||
          loadingBookingP ||
          instructorSessionsLoading ||
          IHLoading ||
          loadingLCHolidays ||
          userEventsLoading ||
          instructorEventsLoading ||
          fetchingProfile
      )
    } else {
      setLoading(false)
      setDisabled(
        pageLoading ||
          myCalendarLoading ||
          loadingBookingP ||
          instructorSessionsLoading ||
          IHLoading ||
          loadingLCHolidays ||
          userEventsLoading ||
          instructorEventsLoading ||
          fetchingProfile
      )
    }
  }, [
    pageLoading,
    myCalendarLoading,
    loadingBookingP,
    instructorSessionsLoading,
    IHLoading,
    loadingLCHolidays,
    userEventsLoading,
    instructorEventsLoading,
    fetchingProfile,
  ])

  useEffect(() => {
    setError(
      !!(
        myCalendarError ||
        instructorSessionsError ||
        bookingPError ||
        IHerror ||
        LCHolidaysError ||
        userEventsError ||
        instructorEventsError
      )
    )
  }, [
    myCalendarError,
    instructorSessionsError,
    bookingPError,
    IHerror,
    LCHolidaysError || userEventsError || instructorEventsError,
  ])

  useEffect(() => {
    const currentDate = moment.tz(week, profile.Timezone).clone().weekday(0).startOf('day')
    const prevStart = range.start.clone().weekday(0)
    const week1 = moment.tz(profile.Timezone).weekday(0)

    if ((currentDate.isBefore(prevStart) && !prevStart.isSame(week1, 'day')) || currentDate.isSameOrAfter(range.end)) {
      const newStart = currentDate.clone().subtract(numOfWeeks, 'week').weekday(0).startOf('day')
      const newEnd = currentDate.clone().add(numOfWeeks, 'week').weekday(7).startOf('day')
      setRange({
        start: newStart.isBefore(moment.tz(profile.Timezone)) ? moment.tz(profile.Timezone) : newStart,
        end: newEnd.isAfter(moment.tz(endDate, profile.Timezone)) ? moment.tz(endDate, profile.Timezone) : newEnd,
      })
    }
  }, [week])

  useEffect(() => {
    if (!instructor) {
      return
    }

    fetchDetails()
  }, [instructor, range])

  const refetchData = async () => {
    await Promise.all([
      IHRefetch(),
      studentSessionRefretch(),
      instructorSessionRefetch(),
      bpRefetch(),
      userEventsRefetch(),
      instructorEventsRefetch(),
      ...(isRegionalUser ? [refetchUserProfile()] : []),
    ])
  }

  useEffect(() => {
    const StudentProgram = program.program

    if (StudentProgram) {
      const lessonsPerSession = StudentProgram.LessonsPerSession || 1
      const lessonsRemaining = isRebook ? lessonsPerSession : StudentProgram.LessonsRemaining
      setMaxSelect(lessonsRemaining / lessonsPerSession)
      setHoverRange(StudentProgram.UnitsPerLesson * MINS_PER_UNIT * lessonsPerSession)
    }

    setMonths(generateMonths())

    return () => {
      dispatch(updateFMIBulkFormInstructors([]))
      dispatch(updateFMIBulkFormInstructorTypes([], [], []))
      dispatch(updateFMIBulkFormSelectedDate(''))
    }
  }, [])

  useEffect(() => {
    if ((isCrossInstructorOnly || isBulkOnlyFeature) && instructors?.length && queryInstructor) {
      const selectedInstructor = instructorLessons?.[instructor]
      setInstructorLessons({
        ...omit(instructorLessons || {}, [instructor]),
        [instructor]: selectedInstructor || {
          name: queryInstructor?.label || '',
          lessons: [],
        },
      })
    }
  }, [instructors, instructor])

  useEffect(() => {
    if (months) {
      setMonth(selectedDate ? moment(selectedDate).format(DATE_FORMAT.MY) : months[0]?.value)
    }
  }, [months, selectedDate])

  useEffect(() => {
    setNotSaved(isBulkOnlyFeature || lessons.length > 0)
  }, [lessons.length, isBulkOnlyFeature])

  useEffect(() => {
    const isURL = typeof notSaved === 'string'
    const confirmationMessage = intl.formatMessage({
      id: 'Any selection you have made may not be saved. Are you sure you want to reload this page?',
      defaultMessage: 'Any selection you have made may not be saved. Are you sure you want to reload this page?',
    })
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      const event = e || window.event
      event.returnValue = confirmationMessage
      return confirmationMessage // Gecko + Webkit, Safari, Chrome etc.
    }
    const beforeRouteHandler = (url: string) => {
      if (router.pathname !== url) {
        setOpenWarningModal(true)
        setToUrl(url)
        // to inform NProgress or something ...
        router.events.emit('routeChangeError')
        // eslint-disable-next-line no-throw-literal
        throw `Route change to "${url}" was aborted (this error can be safely ignored).`
      }
    }
    if (notSaved && !isURL) {
      // Intentional: Availability manages its own unsaved-changes guard via routeChangeStart +
      // throw pattern (see beforeRouteHandler above). It does not use the NavigationContract
      // guard because it needs finer control — it aborts the route event directly and shows
      // its own warning modal. This is a documented exception to the adapter-guard pattern.
      window.addEventListener('beforeunload', beforeUnloadHandler)
      router.events.on('routeChangeStart', beforeRouteHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      router.events.off('routeChangeStart', beforeRouteHandler)

      if (notSaved !== 'back' && isURL) {
        router.push(notSaved as string)
      } else if (isURL) {
        router.back()
      }
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      router.events.off('routeChangeStart', beforeRouteHandler)
    }
  }, [notSaved])

  const memoHeader = useMemo(
    () => (
      <PageHeader
        options={instructors || []}
        onChange={(data) => setInstructor(data)}
        loading={pageLoading}
        defaultValue={headerDefault}
        disabled={loading || disabled}
        notSaved={!isCrossInstructorOnly || !isBulkOnlyFeature ? notSaved : undefined}
        setOpenModal={(data: boolean) => (!isCrossInstructorOnly || !isBulkOnlyFeature) && setOpenWarningModal(data)}
        setToURL={(data: string) => setToUrl(data)}
        instructorId={instructor}
      />
    ),
    [instructors, pageLoading, loading, disabled, notSaved, headerDefault, instructor]
  )

  const memoSidebar = useMemo(
    () => (
      <Sidebar
        loading={loading}
        current={currentSelected}
        selected={selected}
        deselected={deselected}
        failed={failedRanges}
        disabled={disabled}
        disableBookButton={isRebook ? false : program.disableFMIBulkBooking(lessons)} // If rebook, always enable
        deleteAllSilently={deleteAllSilently}
        profile={profile}
        program={program}
        instructor={instructor}
        lessons={lessons}
        setters={setters}
        instructorHours={instructorHours}
        refresh={refetchData}
        readOnly={readOnly}
        instructorLessons={instructorLessons}
      />
    ),
    [
      loading,
      currentSelected,
      selected,
      deselected,
      failedRanges,
      disabled,
      deleteAllSilently,
      instructor,
      lessons,
      instructorHours,
      program,
    ]
  )

  const memoCalendar = useMemo(() => {
    const hasExcessUnbookedLessons = program?.hasExcessUnbookedLessons(selected, isRebook)
    const restricts =
      isCrossInstructorOnly || isBulkOnlyFeature
        ? instructorRestrictions?.[instructor]?.restrictions || []
        : restrictions
    return (
      <SchedulingCalendar
        loading={
          myCalendarLoading ||
          loadingBookingP ||
          instructorSessionsLoading ||
          IHLoading ||
          deleteAllSilently ||
          userEventsLoading ||
          instructorEventsLoading ||
          deletingStoredLessons ||
          fetchingProfile
        }
        timezone={profile.Timezone}
        availabilities={[...(availabilities || []), ...selectedIntervals]}
        unavailabilities={[...unavailabilities, ...restricts].filter(
          (unavail) =>
            !selectedIntervals.some((interval) => interval?.[0] === unavail?.[0] && interval?.[1] === unavail?.[1])
        )}
        bookedSessions={sessions}
        bookedEvents={events}
        endDate={moment.tz(program?.program?.ContractEndDate, profile.Timezone).endOf('day').toISOString()}
        viewDate={viewDate}
        onMonthChange={onMonthChange}
        height="650px"
        bgColor="#F4F8FF"
        cardOffset="4px"
        startOfWeek={0}
        onSelect={createTempHold}
        onDeselect={removeTempHold}
        bookedLabel={intl.formatMessage({ id: 'Booked lesson', defaultMessage: 'Booked lesson' })}
        bookedEventLabel={intl.formatMessage({ id: 'Booked event', defaultMessage: 'Booked event' })}
        message={intl.formatMessage({ id: 'Please wait a moment.', defaultMessage: 'Please wait a moment.' })}
        messageTitle={intl.formatMessage({ id: 'Checking details...', defaultMessage: 'Checking details...' })}
        onChangeWeek={(date: any) => {
          setWeek(date.toISOString())
          onMonthChange(date)
        }}
        maxSelect={readOnly || hasExcessUnbookedLessons || program.IsFMICalendarBulkBookingAllowed ? 0 : maxSelect}
        hoverRange={hoverRange}
        loadingRanges={loadingRanges}
        failedRanges={failedRanges}
        selected={selected}
        withSelectedLabels={isCrossInstructorOnly || isBulkOnlyFeature}
        selectedLabels={selectedLabels}
        selectedActive={instructor}
        waiting={disabled}
        preventSelect={
          selected.length === maxSelect ||
          hasExcessUnbookedLessons ||
          readOnly ||
          program.IsFMICalendarBulkBookingAllowed
        }
        defaultWeek={
          selectedDate
            ? moment(selectedDate, DATE_FORMAT.BASIC).isoWeekday(3).startOf('day').format(DATE_FORMAT.BASIC)
            : moment.tz(profile.Timezone).format(DATE_FORMAT.BASIC)
        }
        timeLabelFormat={getTimeFormat()}
      />
    )
  }, [
    myCalendarLoading,
    loadingBookingP,
    instructorSessionsLoading,
    deleteAllSilently,
    availabilities,
    unavailabilities,
    sessions,
    viewDate,
    maxSelect,
    hoverRange,
    loadingRanges,
    failedRanges,
    selected,
    disabled,
    IHLoading,
    userEventsLoading,
    instructorEventsLoading,
    fetchingProfile,
    state,
    instructor,
    selectedDate,
  ])

  return error ? (
    <Error statusCode={400} />
  ) : (
    <>
      {memoHeader}
      <Spacer size="lg" />
      <Scroller>
        <MenuTabBox>
          <MenuTab activeTab={isBulkOnlyFeature ? 1 : at}>
            {(isBulkOnlyFeature ? [menu[1]] : menu).map((item) => (
              <MenuTabItem smallWidth key={item.tab}>
                {loading ? (
                  <Skeleton width="100px" height="18px" />
                ) : (
                  <Tab
                    onClick={() => {
                      if (item.tab === 1 && !isBulkOnlyFeature) {
                        router.push(`${baseRoute}/profile?${getRouteParams()}`)
                      }
                      setAt(item.tab)
                    }}
                  >
                    {item.child}
                  </Tab>
                )}
              </MenuTabItem>
            ))}
          </MenuTab>
        </MenuTabBox>
      </Scroller>
      <Spacer size="md" />
      <MainContent>
        <LeftContent>
          <UpperSection>
            <StyledSelect>
              {loading ? (
                <Skeleton width="100%" height="36px" style={{ maxWidth: '800px' }} />
              ) : (
                months && (
                  <Select
                    defaultValue={months[0]?.value}
                    value={month}
                    onChange={(data) => {
                      const date = moment.tz(data, profile.Timezone)
                      setMonth(data)
                      setViewDate(data)
                      setWeek(date.toISOString())
                    }}
                    options={months}
                    disableMargin
                    disabled={disabled}
                  />
                )
              )}
            </StyledSelect>
            <LegendWrapper>
              <LegendItem icon={<Session />} text={'Existing booked lesson'} loading={loading} />
              <LegendItem icon={<AvailableTimeSlot />} text={'Available'} loading={loading} width="50px" />
              <LegendItem icon={<UnavailableTimeSlot />} text={'Unavailable'} loading={loading} width="50px" />
            </LegendWrapper>
          </UpperSection>
          <Spacer size="xxxs" />
          <Spacer size="xs" />
          <HR />

          {isBulkOnlyFeature ? (
            <Instructors
              defaultInstructorId={instructor}
              loading={loading}
              disabled={disabled}
              isMobile
              onChange={(InstructorId) => setInstructor(InstructorId)}
            />
          ) : null}

          {isBulkOnlyFeature ? (
            <Instructors
              defaultInstructorId={instructor}
              loading={loading}
              disabled={disabled}
              onChange={(InstructorId) => setInstructor(InstructorId)}
            >
              {memoCalendar}
            </Instructors>
          ) : (
            memoCalendar
          )}
          <Spacer size="md" />
        </LeftContent>
        <SideContent>{memoSidebar}</SideContent>
      </MainContent>
      <Spacer size="lg" />
      <WarningModal
        open={openWarningModal}
        onClose={() => {
          setOpenWarningModal(false)
        }}
        onCancel={() => {
          setOpenWarningModal(false)
        }}
        title={intl.formatMessage({ id: 'Unbooked lessons', defaultMessage: 'Unbooked lessons' })}
        description={
          <>
            <div>
              {intl.formatMessage({
                id: 'Are you sure you want to leave this page?',
                defaultMessage: 'Are you sure you want to leave this page?',
              })}
            </div>
            <div>
              {intl.formatMessage({
                id: `Book your lessons now so you don't miss out!`,
                defaultMessage: `Book your lessons now so you don't miss out!`,
              })}
            </div>
          </>
        }
        cancelOutline
        cancelLabel={intl.formatMessage({ id: 'Stay on this page', defaultMessage: 'Stay on this page' })}
        proceedLabel={intl.formatMessage({ id: 'Leave this page', defaultMessage: 'Leave this page' })}
        onProceed={() => {
          if (toURL?.includes('id:')) {
            setHeaderDefault(toURL.split(':')[1])
            setInstructor(toURL.split(':')[1])
          } else {
            setNotSaved(toURL || '/')
          }
          setOpenWarningModal(false)
          setDeleteAllSilently(true)
          setSelectedLabels(undefined)
        }}
      />
    </>
  )
}

export default InstructorAvailability
